import axios from 'axios';
import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCog, FaImage, FaSpinner, FaSave, FaTrash, FaUpload, FaPalette, FaBell, FaTimes, FaVolumeUp } from "react-icons/fa";
// Toast removed per request; using alert()

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function SettingsPage() {
    const navigate = useNavigate();
    const [imageUploading, setImageUploading] = useState({
        image1: false,
        image2: false,
        image3: false,
        image4: false
    });
    const [imgData, setImgData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('banner');
    const [generalSettings, setGeneralSettings] = useState({
        appName: 'Kipps Sweets',
        contactEmail: 'contact@kippssweets.in',
        contactPhone: '+91 1234567890',
        themeColor: 'red',
        currency: 'INR',
        timezone: 'Asia/Kolkata'
    });
    const token = localStorage.getItem('authToken');

    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong');
    };

    const validateAndUpload = async (file, imageKey) => {
        if (!file) return;
        const allowedTypes = ['image/jpeg','image/jpg','image/png','image/webp','image/avif'];
    if (!allowedTypes.includes(file.type)) { alert('Only JPG, PNG, WebP, AVIF images allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('Max file size 5MB'); return; }
        try {
            setImageUploading(prev => ({ ...prev, [imageKey]: true }));
            const formData = new FormData();
            formData.append('image', file);
            const response = await axios.post(`${API_BASE_URL}/upload-prod-img`, formData, { headers: { ...authHeaders } });
            await axios.patch(`${API_BASE_URL}/update-banner-images`, { [imageKey]: response.data.path }, { headers: { ...authHeaders } });
            alert(`Updated ${imageKey}`);
            await handleFetchImages();
        } catch (err) {
            handleApiError(err);
        } finally {
            setImageUploading(prev => ({ ...prev, [imageKey]: false }));
        }
    };

    const handleImageUpload = async (event) => {
        const imageKey = event.target.name;
        const file = event.target.files[0];
        validateAndUpload(file, imageKey);
    };

    const handleRemoveImage = async (imageKey) => {
    if (!imgData[0]?.[imageKey]) { alert('Nothing to remove'); return; }
        try {
            setImageUploading(prev => ({ ...prev, [imageKey]: true }));
            await axios.patch(`${API_BASE_URL}/update-banner-images`, { [imageKey]: '' }, { headers: { ...authHeaders } });
            alert('Image removed');
            await handleFetchImages();
        } catch (err) {
            handleApiError(err);
        } finally {
            setImageUploading(prev => ({ ...prev, [imageKey]: false }));
        }
    };

    const onDrop = useCallback((e, imageKey) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) validateAndUpload(file, imageKey);
    }, []);

    const handleFetchImages = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/slider-images`, { headers: { ...authHeaders } });
            setImgData(response.data);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleFetchImages();
        try {
            const stored = localStorage.getItem('adminGeneralSettings');
            if (stored) setGeneralSettings(prev => ({ ...prev, ...JSON.parse(stored) }));
        } catch {}
    }, []);

    const handleGeneralChange = (e) => {
        const { name, value } = e.target;
        setGeneralSettings(prev => ({ ...prev, [name]: value }));
    };

    const saveGeneralSettings = () => {
        const phone = generalSettings.contactPhone.trim();
        if (!/^\+?[0-9\s-]{6,20}$/.test(phone)) { alert('Invalid phone number format'); return; }
        localStorage.setItem('adminGeneralSettings', JSON.stringify(generalSettings));
        alert('Settings saved');
    };

    const renderBannerSettings = () => (
        <section>
            <header className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
                <div>
                    <h5 className="mb-1 d-flex align-items-center fw-bold text-dark"><FaImage className="text-danger me-2"/>Banner Management</h5>
                    <small className="text-muted">Manage homepage banner images (1920x600 recommended)</small>
                </div>
            </header>
            <div className="row g-3">
                {['image1','image2','image3','image4'].map((imageKey, index)=>(
                    <div className="col-lg-6 col-md-6" key={imageKey}>
                        <div 
                            className="banner-card-square position-relative h-100 d-flex flex-column" 
                            onDragOver={(e)=>e.preventDefault()} 
                            onDrop={(e)=>onDrop(e,imageKey)}
                        >
                            <div className="d-flex justify-content-between align-items-center p-3 border-bottom" style={{ backgroundColor: '#FEF2F2', borderColor: '#cbd5e1' }}>
                                <span className="fw-bold small text-uppercase text-dark">Banner {index+1}</span>
                                <div className="d-flex gap-2">
                                    <button 
                                        type="button" 
                                        className="save-brand-btn py-1 px-3" 
                                        style={{ fontSize: '0.8rem', height: '32px' }}
                                        disabled={imageUploading[imageKey]} 
                                        onClick={()=>document.querySelector(`#file-${imageKey}`).click()}
                                    >
                                        {imageUploading[imageKey] ? <FaSpinner className="fa-spin"/> : <><FaUpload className="me-1"/> Upload</>}
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn btn-outline-danger py-1 px-3 rounded-0" 
                                        style={{ fontSize: '0.8rem', height: '32px', borderColor: '#cbd5e1' }}
                                        onClick={()=>handleRemoveImage(imageKey)} 
                                        disabled={imageUploading[imageKey]}
                                    > 
                                        <FaTrash/> 
                                    </button>
                                </div>
                            </div>
                            <div className="flex-grow-1 d-flex align-items-center justify-content-center preview-wrapper p-3 bg-white">
                                {imageUploading[imageKey] ? (
                                    <div className="upload-skel w-100 h-100 d-flex flex-column align-items-center justify-content-center py-4">
                                        <FaSpinner className="fa-spin mb-2 text-danger"/>
                                        <small className="text-muted">Uploading...</small>
                                    </div>
                                ) : loading ? (
                                    <div className="skeleton-block w-100 h-100 py-4"/>
                                ) : imgData[0]?.[imageKey] ? (
                                    <img
                                        src={
                                            imgData[0][imageKey]
                                                ? (imgData[0][imageKey].startsWith('http')
                                                    ? imgData[0][imageKey]
                                                    : `${API_BASE_URL}${imgData[0][imageKey].startsWith('/') ? imgData[0][imageKey] : '/' + imgData[0][imageKey]}`)
                                                : ''
                                        }
                                        alt={`Banner ${index+1}`}
                                        className="img-fluid border"
                                        style={{maxHeight:'160px', width:'100%', objectFit:'cover', borderColor: '#cbd5e1'}} 
                                        onError={(e)=>{e.currentTarget.style.opacity='0.4';}}
                                    />
                                ) : (
                                    <div className="text-center text-muted small py-4">
                                        <FaImage className="fs-1 mb-2 text-secondary opacity-50"/>
                                        <div className="fw-semibold">Drop image here or click Upload</div>
                                    </div>
                                )}
                            </div>
                            <input id={`file-${imageKey}`} type="file" accept="image/*" name={imageKey} onChange={handleImageUpload} className="d-none" />
                            <div className="hint small text-muted px-3 pb-3 border-top pt-2" style={{ borderColor: '#f1f5f9', fontSize: '0.78rem' }}>
                                JPG / PNG / WebP / AVIF • Max file size 5MB
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );

    const renderGeneralSettings = () => (
        <section>
            <header className="mb-3">
                <h5 className="mb-1 d-flex align-items-center fw-bold text-dark"><FaCog className="text-danger me-2"/>General Settings</h5>
                <small className="text-muted">Basic application settings</small>
            </header>
            <div className="row g-3">
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label text-dark fw-bold small text-uppercase">Application Name</label>
                        <input type="text" className="form-control rounded-0" style={{ borderColor: '#cbd5e1', height: '42px', fontSize: '0.88rem' }} name="appName" value={generalSettings.appName} onChange={handleGeneralChange} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label text-dark fw-bold small text-uppercase">Contact Email</label>
                        <input type="email" className="form-control rounded-0" style={{ borderColor: '#cbd5e1', height: '42px', fontSize: '0.88rem' }} name="contactEmail" value={generalSettings.contactEmail} onChange={handleGeneralChange} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label text-dark fw-bold small text-uppercase">Contact Phone</label>
                        <input type="tel" className="form-control rounded-0" style={{ borderColor: '#cbd5e1', height: '42px', fontSize: '0.88rem' }} name="contactPhone" value={generalSettings.contactPhone} onChange={handleGeneralChange} />
                        <small className="text-muted mt-1 d-block" style={{ fontSize: '0.78rem' }}>Digits, +, spaces, - allowed</small>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label text-dark fw-bold small text-uppercase">Theme Color</label>
                        <select className="form-select rounded-0" style={{ borderColor: '#cbd5e1', height: '42px', fontSize: '0.88rem' }} name="themeColor" value={generalSettings.themeColor} onChange={handleGeneralChange}>
                            <option value="red">Red (Default)</option>
                            <option value="green">Green</option>
                            <option value="purple">Purple</option>
                            <option value="orange">Orange</option>
                        </select>
                    </div>
                    <div className="mb-3">
                        <label className="form-label text-dark fw-bold small text-uppercase">Currency</label>
                        <select className="form-select rounded-0" style={{ borderColor: '#cbd5e1', height: '42px', fontSize: '0.88rem' }} name="currency" value={generalSettings.currency} onChange={handleGeneralChange}>
                            <option value="INR">Indian Rupee (₹)</option>
                            <option value="USD">US Dollar ($)</option>
                            <option value="EUR">Euro (€)</option>
                        </select>
                    </div>
                    <div className="mb-3">
                        <label className="form-label text-dark fw-bold small text-uppercase">Timezone</label>
                        <select className="form-select rounded-0" style={{ borderColor: '#cbd5e1', height: '42px', fontSize: '0.88rem' }} name="timezone" value={generalSettings.timezone} onChange={handleGeneralChange}>
                            <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                            <option value="UTC">UTC</option>
                            <option value="America/New_York">America/New_York</option>
                        </select>
                    </div>
                </div>
            </div>
            <div className="text-end mt-2">
                <button className="save-brand-btn py-2 px-4" onClick={saveGeneralSettings}>
                    <FaSave className="me-2"/>Save Settings
                </button>
            </div>
        </section>
    );

    // Single new order notification with sound (SSE based)
    const [newOrderEnabled, setNewOrderEnabled] = useState(()=>{
        try { return JSON.parse(localStorage.getItem('newOrderEnabled')) ?? true; } catch { return true; }
    });
    const [lastOrder, setLastOrder] = useState(null);
    const audioRef = React.useRef(null);

    useEffect(()=>{
        localStorage.setItem('newOrderEnabled', JSON.stringify(newOrderEnabled));
    },[newOrderEnabled]);

    useEffect(()=>{
        const src = `${API_BASE_URL.replace(/\/$/,'')}/order-stream`;
        const es = new EventSource(src);
        es.onmessage = (e)=>{
            try {
                const data = JSON.parse(e.data);
                if (data.type === 'new_order' && newOrderEnabled) {
                    setLastOrder({ id: data.id, total: data.totalAmount, time: data.createdAt });
                    if (audioRef.current) { audioRef.current.currentTime = 0; audioRef.current.play().catch(()=>{}); }
                    alert(`New order: #${data.id?.slice(-6)} Amount: ₹${data.totalAmount}`);
                }
            } catch {}
        };
        es.onerror = ()=>{};
        return ()=> es.close();
    },[newOrderEnabled]);

    const testSound = () => {
        if (audioRef.current) { audioRef.current.currentTime = 0; audioRef.current.play().catch(()=>alert('Autoplay blocked')); }
    };

    const renderNotificationSettings = () => (
        <section>
            <header className="mb-3">
                <h5 className="mb-1 d-flex align-items-center fw-bold text-dark"><FaBell className="text-danger me-2"/>New Order Notification</h5>
                <small className="text-muted">Real-time alert (SSE) with optional sound</small>
            </header>
            <div className="form-check form-switch mb-3">
                <input className="form-check-input" type="checkbox" id="enableNewOrder" checked={newOrderEnabled} onChange={()=>setNewOrderEnabled(v=>!v)} />
                <label className="form-check-label fw-semibold text-dark" htmlFor="enableNewOrder">Enable new order alerts</label>
            </div>
            <div className="d-flex align-items-center gap-3 mb-3">
                <button type="button" className="report-export-btn py-1 px-3" onClick={testSound}>
                    <FaVolumeUp className="me-1"/>Test Sound
                </button>
                {lastOrder && <span className="small text-muted fw-medium">Last order: #{String(lastOrder.id).slice(-6)} ₹{lastOrder.total}</span>}
            </div>
            <audio ref={audioRef} preload="auto">
                <source src="https://actions.google.com/sounds/v1/alarms/beep_short.ogg" type="audio/ogg" />
            </audio>
            <div className="alert border small mb-0 rounded-0" style={{ backgroundColor: '#FEF2F2', borderColor: '#cbd5e1', color: '#991b1b' }}>
                Keep this page open to receive live new order alerts.
            </div>
        </section>
    );

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row mb-2">
                    <div className="col-12">
                        <div className="section-title">
                            <h4 className="mt-0">ADMIN SETTINGS</h4>
                            <p className="text-muted p-0 m-0">Manage application settings and configurations</p>
                        </div>
                    </div>
                </div>

                {/* Settings Card Shell - Edge To Edge */}
                <div 
                    className="card mb-4" 
                    style={{ 
                        borderTop: '1px solid #cbd5e1', 
                        borderBottom: '1px solid #cbd5e1', 
                        borderLeft: 'none', 
                        borderRight: 'none', 
                        borderRadius: 0, 
                        boxShadow: 'none', 
                        marginLeft: '-1rem', 
                        marginRight: '-1rem' 
                    }}
                >
                    <div 
                        className="card-header d-flex align-items-center justify-content-start" 
                        style={{ backgroundColor: '#FEF2F2', borderBottom: '1px solid #cbd5e1', height: '56px', padding: '0 1rem' }}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <button 
                                type="button" 
                                className={`settings-tab-btn ${activeTab === 'banner' ? 'active' : ''}`}
                                onClick={() => setActiveTab('banner')}
                            >
                                <FaImage /> Banner Management
                            </button>
                            <button 
                                type="button" 
                                className={`settings-tab-btn ${activeTab === 'general' ? 'active' : ''}`}
                                onClick={() => setActiveTab('general')}
                            >
                                <FaCog /> General Settings
                            </button>
                            <button 
                                type="button" 
                                className={`settings-tab-btn ${activeTab === 'notifications' ? 'active' : ''}`}
                                onClick={() => setActiveTab('notifications')}
                            >
                                <FaBell /> Notifications
                            </button>
                        </div>
                    </div>
                    <div className="card-body p-4 bg-white">
                        {activeTab === 'banner' && renderBannerSettings()}
                        {activeTab === 'general' && renderGeneralSettings()}
                        {activeTab === 'notifications' && renderNotificationSettings()}
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

export default SettingsPage;
